import React, { useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Flex,
} from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { BILLING_TYPES } from "./BillingDataModal";

// Types 2, 3, 4 require company fields; 1 only needs name + ID.
const COMPANY_TYPES = ["2", "3", "4"];

interface Props {
  /** Legacy-shape address (from `fromV1` adapter) being edited, or null when creating. */
  address: any | null;
  onClose: () => void;
  /**
   * Emits the updated address in the legacy shape the popup's `handleAgendaUpdate`
   * expects (`firstname`, `lastname`, `company`, `vatId`, `billingTypeId`,
   * `city`, `region`, `postcode`, plus `userAddressId` when editing).
   */
  onUpdate: (updated: any) => void;
}

export const QuoteBillingAgendaForm: React.FC<Props> = ({
  address,
  onClose,
  onUpdate,
}) => {
  const [form] = Form.useForm();
  const billingType = Form.useWatch("billingType", form);
  const isCompanyType = COMPANY_TYPES.includes(billingType);

  useEffect(() => {
    const bt = address?.billingTypeId
      ? String(address.billingTypeId)
      : address?.billingType?.value
        ? String(address.billingType.value)
        : "1";
    const isCompany = COMPANY_TYPES.includes(bt);
    const fullName = address
      ? `${address.firstname || ""} ${address.lastname || ""}`.trim() ||
        address.company ||
        ""
      : "";

    form.setFieldsValue({
      billingType: bt,
      fullName: !isCompany ? fullName : "",
      documentId: !isCompany ? address?.vatId || "" : "",
      companyName: isCompany ? address?.company || "" : "",
      taxId: isCompany ? address?.vatId || "" : "",
      taxDirectorate: address?.taxDirectorate || address?.street || "",
      city: address?.city || "",
      state: address?.region || "",
      zipCode: address?.postcode || "",
    });
  }, [address, form]);

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const bt = Number(values.billingType);
      const isCompany = COMPANY_TYPES.includes(values.billingType);

      const base = {
        ...address,
        billingTypeId: bt,
      };

      if (isCompany) {
        onUpdate({
          ...base,
          company: values.companyName || "",
          vatId: values.taxId || "",
          firstname: "",
          lastname: "",
          street: values.taxDirectorate || "",
          city: values.city || "",
          region: values.state || "",
          postcode: values.zipCode || "",
        });
        return;
      }

      // Consumer Final: split fullName into firstname/lastname; address
      // fields stay empty (the entity treats them as nullable for billing).
      const nameParts = (values.fullName || "").trim().split(/\s+/);
      const firstname = nameParts[0] || "";
      const lastname = nameParts.slice(1).join(" ") || "";

      onUpdate({
        ...base,
        company: (values.fullName || "").trim(),
        vatId: values.documentId || "",
        firstname,
        lastname,
        street: "",
        city: "",
        region: "",
        postcode: "",
      });
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      size="small"
      preserve={false}
      initialValues={{ billingType: "1" }}
    >
      <Form.Item
        label="Tipo de factura *"
        name="billingType"
        rules={[{ required: true, message: "Seleccione tipo de factura" }]}
      >
        <Select options={BILLING_TYPES} />
      </Form.Item>

      {!isCompanyType && (
        <Flex gap={16}>
          <Form.Item
            label="Nombre y apellido *"
            name="fullName"
            style={{ flex: 1 }}
            rules={[{ required: true, message: "Requerido" }]}
          >
            <Input placeholder="Nombre y apellido" />
          </Form.Item>
          <Form.Item
            label="DNI *"
            name="documentId"
            style={{ flex: 1 }}
            rules={[{ required: true, message: "Requerido" }]}
          >
            <Input placeholder="Ingresá el DNI" />
          </Form.Item>
        </Flex>
      )}

      {isCompanyType && (
        <>
          <Flex gap={16}>
            <Form.Item
              label="Razón social *"
              name="companyName"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "Requerido" }]}
            >
              <Input placeholder="Razón social" />
            </Form.Item>
            <Form.Item
              label="CUIT *"
              name="taxId"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "Requerido" }]}
            >
              <Input placeholder="Ingresá el CUIT" />
            </Form.Item>
          </Flex>
          <Flex gap={16}>
            <Form.Item
              label="Domicilio fiscal *"
              name="taxDirectorate"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "Requerido" }]}
            >
              <Input placeholder="Ingresá el domicilio fiscal" />
            </Form.Item>
            <Form.Item
              label="Ciudad *"
              name="city"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "Requerido" }]}
            >
              <Input placeholder="Ingresá la ciudad" />
            </Form.Item>
          </Flex>
          <Flex gap={16}>
            <Form.Item
              label="Provincia *"
              name="state"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "Requerido" }]}
            >
              <Input placeholder="Ingresá la provincia" />
            </Form.Item>
            <Form.Item
              label="Código postal *"
              name="zipCode"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "Requerido" }]}
            >
              <Input placeholder="Código postal" />
            </Form.Item>
          </Flex>
        </>
      )}

      <Flex justify="end" gap={8} style={{ marginTop: 8 }}>
        <Button danger onClick={onClose}>
          Cancelar
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          icon={<CheckOutlined />} iconPosition="end"
        >
          Guardar
        </Button>
      </Flex>
    </Form>
  );
};
